import { authTokenManager } from "@legal-500/auth-client";
import * as api from "./client.js";

const WP_SYNCER_BASE = "/v1/platform/wp-syncer";

export type WpSyncerJobStatus =
  | { state: "queued" }
  | { state: "running"; startedAt: number }
  | { state: "succeeded"; durationMs: number }
  | { state: "failed"; error: string };

export type TriggerWpSyncerBody = {
  force?: boolean;
  dryRun?: boolean;
};

export type TriggerWpSyncerResponse = {
  runId: string;
  requestId: string;
};

export type WpSyncerStatusResponse = {
  runId: string;
  status: WpSyncerJobStatus;
  requestId: string;
};

export type CancelWpSyncerResponse = {
  runId: string;
  cancelled: boolean;
  reason?: string;
  requestId: string;
};

export type WpSyncerRunRawStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export type WpSyncerRunSource = "portal" | "azure";

export type WpSyncerRunSummary = {
  runId: string;
  status: WpSyncerRunRawStatus;
  startedAt: number | null;
  finishedAt: number | null;
  durationMs: number | null;
  executionName?: string;
  source?: WpSyncerRunSource;
  triggeredBy?: string;
};

export type ListWpSyncerRunsResponse = {
  runs: WpSyncerRunSummary[];
  requestId: string;
};

export type ResetWpSyncerStateResponse = {
  clearedRuns: number;
  requestId: string;
};

export type WpSyncerLogHandlers = {
  onLine: (line: string) => void;
  onEnd: (state: string) => void;
  onError?: (error: Error) => void;
};

export type WpSyncerLogSubscription = {
  close: () => void;
};

export type WpSyncerApiError = Error & {
  status?: number;
  code?: string;
  detailsRaw?: unknown;
};

export function isWpSyncerConflictError(error: unknown): error is WpSyncerApiError {
  return error instanceof Error && (error as WpSyncerApiError).status === 409;
}

/** Extracts the runId of the already-in-flight sync from a trigger 409's error.details. */
export function extractConflictingRunId(error: WpSyncerApiError): string | null {
  const details = error.detailsRaw;
  if (details && typeof details === "object" && "existingRunId" in details) {
    const value = (details as { existingRunId?: unknown }).existingRunId;
    return typeof value === "string" ? value : null;
  }
  return null;
}

export const wpSyncerService = {
  listRuns(limit = 50): Promise<ListWpSyncerRunsResponse> {
    return api.get<ListWpSyncerRunsResponse>(`${WP_SYNCER_BASE}/syncs?limit=${limit}`);
  },

  trigger(body: TriggerWpSyncerBody): Promise<TriggerWpSyncerResponse> {
    return api.post<TriggerWpSyncerResponse>(`${WP_SYNCER_BASE}/syncs`, body);
  },

  getStatus(runId: string): Promise<WpSyncerStatusResponse> {
    return api.get<WpSyncerStatusResponse>(`${WP_SYNCER_BASE}/syncs/${runId}`);
  },

  cancel(runId: string): Promise<CancelWpSyncerResponse> {
    return api.del<CancelWpSyncerResponse>(`${WP_SYNCER_BASE}/syncs/${runId}`);
  },

  resetState(): Promise<ResetWpSyncerStateResponse> {
    return api.post<ResetWpSyncerStateResponse>(`${WP_SYNCER_BASE}/syncs/reset`);
  },

  subscribeToLog(runId: string, handlers: WpSyncerLogHandlers): WpSyncerLogSubscription {
    const url = api.url(`${WP_SYNCER_BASE}/syncs/${runId}/log`);
    const token = authTokenManager.getToken();
    const fullUrl = token ? `${url}?token=${encodeURIComponent(token)}` : url;
    const eventSource = new EventSource(fullUrl, { withCredentials: true });

    eventSource.onmessage = event => {
      handlers.onLine(event.data);
    };

    eventSource.addEventListener("end", event => {
      const message = event instanceof MessageEvent ? event.data : "completed";
      handlers.onEnd(typeof message === "string" ? message : "completed");
      eventSource.close();
    });

    eventSource.onerror = () => {
      handlers.onError?.(new Error(`SSE connection error for wp-syncer run ${runId}`));
    };

    return { close: () => eventSource.close() };
  },
};
